import { computed, watch, onUnmounted, unref, ComputedRef, Ref } from 'vue';
import { useInterval, useThrottleFn } from '@vueuse/core';
import { useLiveQueryV2, CODES } from '@libs/use';
import { omitBy } from '@libs/utils';

import * as API from '../requests';
import {
  SYMBOL_TRADE_CHANNEL_PREFIX,
  INTERVAL_TIME_MILLISECOND,
  SYMBOL_QUERY_KEYS,
  TRADING_STATE_CODES,
} from '../constants';
import { transformTradePriceChange } from '../utils/transforms';

type Nominal<T, Name extends string> = T & {
  /** The 'name' or species of the nominal. */
  [Symbol.species]: Name;
};
type UTCTimestamp = Nominal<number, 'UTCTimestamp'>;

export type SymbolCandleStickLineChartDataType = {
  value: number | null;
  time: UTCTimestamp;
}[];

export default function useGetSymbolCandleStick({
  isin,
  selectedTimespan,
  selectedChartFilters,
  tradeState,
}: {
  isin: ComputedRef<string>;
  selectedTimespan: Ref<string>;
  selectedChartFilters: ComputedRef;
  tradeState: ComputedRef<number>;
}) {
  const selectedTimespanRawValue = unref(selectedTimespan);

  const {
    result,
    execute: getSymbolCandleStick,
    isPending: isLoadingSymbolCandleStick,
    isRevalidating: isRevalidatingSymbolCandleStick,
    isError: isErrorSymbolCandleStick,
    isSuccess: isSuccessSymbolCandleStick,
    ...query
  } = useLiveQueryV2({
    fetcher: () =>
      API.getSymbolCandleStick({
        isin: isin.value,
        ...unref(selectedChartFilters),
      }),
    immediate: false,
    key: SYMBOL_QUERY_KEYS.candleStick(selectedTimespan, isin),
    cacheTime: 10,
    getChannels() {
      return [`${SYMBOL_TRADE_CHANNEL_PREFIX}${unref(isin)}`];
    },
    onUpdate(signal) {
      const actions = {
        [CODES.SymbolTradePriceChangeSignal]: transformTradePriceChange,
      };

      if (
        actions[signal.code] &&
        result.value?.data.candleStickDetails?.length
      ) {
        const data = omitBy(
          actions[signal.code](signal.data),
          (v) => v === null || v === undefined
        );

        const { c: lastCandleStickItemLastTradePrice } =
          result.value.data.candleStickDetails[
            result.value.data.candleStickDetails.length - 1
          ];

        if (
          signal.code === CODES.SymbolTradePriceChangeSignal &&
          lastCandleStickItemLastTradePrice !== data.lastTradePrice &&
          isLiveTime.value &&
          unref(isUpdateDataByInterval)
        ) {
          const throttledFn = useThrottleFn(() => {
            addNewData({
              price: data.lastTradePrice,
              time: generateTimestamp(data.lastTradeDate),
            });
          }, 1000);

          throttledFn();
        }
      }
    },
  });

  const symbolCandleStickLineChartData = computed<
    SymbolCandleStickLineChartDataType | undefined
  >(() => {
    return (
      result.value?.data.candleStickDetails?.map((el) => {
        const timestamp = new Date(el.oTime).getTime() / 1000;

        return {
          value: el.c,
          time: timestamp as UTCTimestamp,
        };
      }) || []
    );
  });

  const isUpdateDataByInterval = computed(() => {
    return (
      selectedTimespanRawValue === 'oneDay' ||
      selectedTimespanRawValue === 'oneWeek'
    );
  });

  const isLiveTime = computed(
    () => tradeState.value === TRADING_STATE_CODES.open
  );

  const generateTimestamp = (date: null | number = null) => {
    const time = date ? new Date(date) : new Date();

    time.setSeconds(0);
    time.setMilliseconds(0);

    return time.getTime();
  };

  const { reset, resume, pause } = useInterval(
    INTERVAL_TIME_MILLISECOND[selectedTimespanRawValue],
    {
      controls: true,
      immediate: isUpdateDataByInterval.value,
      callback: () => {
        if (!isUpdateDataByInterval.value || !isLiveTime.value) {
          pause();

          return;
        }

        if (result.value?.data.candleStickDetails?.length) {
          const lastCandleStickItemLastTrade =
            result.value?.data.candleStickDetails[
              result.value?.data.candleStickDetails.length - 1
            ];

          const timeDifference =
            generateTimestamp() -
            generateTimestamp(lastCandleStickItemLastTrade.oTime);

          if (
            timeDifference >
              INTERVAL_TIME_MILLISECOND[selectedTimespanRawValue] ||
            timeDifference ===
              INTERVAL_TIME_MILLISECOND[selectedTimespanRawValue]
          ) {
            addNewData({ time: generateTimestamp(), price: null });
          }
        }
      },
    }
  );

  function addNewData({ price, time }: { time: number; price: number | null }) {
    if (result.value?.data.candleStickDetails) {
      const lastCandleStickItem =
        result.value?.data.candleStickDetails[
          result.value?.data.candleStickDetails.length - 1
        ];

      const generatedNewCandleStickData = {
        o: 0,
        h: 0,
        l: 0,
        c: price ? price : lastCandleStickItem.c,
        v: 0,
        oTime:
          generateTimestamp(lastCandleStickItem.oTime) < time
            ? time
            : generateTimestamp(lastCandleStickItem.oTime),
      };

      if (
        generatedNewCandleStickData.oTime ===
        generateTimestamp(lastCandleStickItem.oTime)
      ) {
        Object.assign(lastCandleStickItem, generatedNewCandleStickData);
      } else {
        result.value?.data.candleStickDetails.push(generatedNewCandleStickData);
      }
    }
  }

  onUnmounted(() => {
    reset();
  });

  watch(
    () => selectedTimespan.value,
    () => {
      resume();
    }
  );

  return {
    getSymbolCandleStick,
    symbolCandleStickLineChartData,
    isLoadingSymbolCandleStick,
    isRevalidatingSymbolCandleStick,
    isErrorSymbolCandleStick,
    isSuccessSymbolCandleStick,
    ...query,
  };
}
